import { MONTHS_NAME, SHORT_MONTHS_NAME } from "../config/params";

/* eslint-disable max-len */
import { DateArray } from "ics";
import { addDays, format, parseISO } from "date-fns";

/**
 * @param {Date} date
 * @return {Number} unix timestamp
 */
export const getUnixTimestamp = (date: Date): number => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return Date.parse(`${year}/${month}/${day} 00:00:00 GMT`) / 1000;
};

/**
 * Timezone-aware variant of getUnixTimestamp.
 * Returns midnight GMT for the calendar date of a unix timestamp
 * interpreted in the given timezone.
 * @param {number} unixSeconds - Unix timestamp in seconds
 * @param {string} timeZone - IANA timezone string (e.g. "America/Chicago")
 * @return {number} Midnight GMT epoch for the calendar date
 */
export const getDateEpochInTimezone = (
  unixSeconds: number,
  timeZone: string,
): number => {
  const date = new Date(unixSeconds * 1000);
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone,
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");

  return Date.parse(`${year}/${month}/${day} 00:00:00 GMT`) / 1000;
};

/**
 * @param {Number} unixTimestamp
 * @return {String} string mm/dd/yyyy
 */
export const fromUnixToDate = (unixTimestamp: number) => {
  const date = new Date(unixTimestamp * 1000);

  return `
    ${("0" + (date.getUTCMonth() + 1)).slice(-2)}/
    ${("0" + date.getUTCDate()).slice(-2)}/
    ${(date.getUTCFullYear())}
  `.replace(/(\r\n|\n|\r)| /gm, "") as unknown as Date;
};
export const fromUnixToUtcDate = (unixTimestamp: number) => {
  const date = new Date(unixTimestamp * 1000);

  return `
    ${("0" + (date.getUTCMonth() + 1)).slice(-2)}/
    ${("0" + date.getUTCDate()).slice(-2)}/
    ${(date.getUTCFullYear())}
  `.replace(/(\r\n|\n|\r)| /gm, "") as unknown as Date;
};

export const fromUnixToMonthDay = (unixTimestamp: number) => {
  const date = new Date(unixTimestamp * 1000);

  return `
    ${("0" + (date.getUTCMonth() + 1)).slice(-2)}/
    ${("0" + date.getUTCDate()).slice(-2)}
  `.replace(/(\r\n|\n|\r)| /gm, "") as unknown as Date;
};

export const fromUnixToDateString = (unixTimestamp: number, timeZone?: string) => {
  const date = new Date(unixTimestamp * 1000);

  return date.toLocaleString("en-US", { timeZone: timeZone });
};

export const fromUnixToFormatDate = (unixTimestamp: string) => {
  if (isNaN(+unixTimestamp)) {
    return unixTimestamp;
  }
  const date = new Date(+unixTimestamp * 1000);

  return `${MONTHS_NAME[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

export const fromISOToFormatDate = (isoTimestamp: string) => {
  if (isoTimestamp === "-") {
    return isoTimestamp;
  }
  const date = (isoTimestamp).substring(0, 10);
  const year = date.substring(0, 4);
  const month = date.substring(5, 7);
  const day = date.substring(8, 10);

  return (`${MONTHS_NAME[parseInt(month)-1]} ${day}, ${year}`);
};

export const fromISOToFormatDatePlusDays = (isoTimestamp: string, days: number) => {
  if (!isoTimestamp || isoTimestamp === "-") {
    return isoTimestamp;
  }

  return format(addDays(parseISO(isoTimestamp), days), "MMMM dd, yyyy");
};

export const fromISOToFullDate = (isoTimestamp: string) => {
  if (isoTimestamp === "-") {
    return isoTimestamp;
  }
  const dateObj = new Date(isoTimestamp);
  const day = dateObj.toLocaleString("en-US", { weekday: "long" });
  const date = (isoTimestamp).substring(0, 10);
  const currentDate = dateObj.getDate();
  const month = date.substring(5, 7);

  return (`${day}, ${MONTHS_NAME[parseInt(month)-1]} ${currentDate}`);
};

export const fromISOToFullDateObject = (isoTimestamp: string) => {
  const dateObj = new Date(isoTimestamp);
  const day = dateObj.toLocaleString("en-US", { weekday: "long" });
  const date = (isoTimestamp).substring(0, 10);
  const currentDate = dateObj.getDate();
  const month = date.substring(5, 7);

  const time = isoTimestamp.substring(11, 16);
  const hour = parseInt(time.substring(0, 2));
  const minute = time.substring(3, 5);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;

  return { day, monthName: MONTHS_NAME[parseInt(month) - 1], shortMonthName: SHORT_MONTHS_NAME[parseInt(month) - 1], currentDate, minute, formattedHour, period };
};

export const fromISOToHour = (isoTimestamp: string) => {
  if (isoTimestamp === "-") {
    return isoTimestamp;
  }
  const time = isoTimestamp.substring(11, 16);
  const hour = parseInt(time.substring(0, 2));
  const minute = time.substring(3, 5);
  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;

  return (`${formattedHour}:${minute} ${period}`);
};


/**
 * @param {Number} unixTimestamp
 * @param {String} timeZone
 * @return {String} string hh:MM a
 */
// TODO: timeZone is optional (timeZone?: string in ILocationItemResponse).
// When undefined, Intl.DateTimeFormat falls back to browser-local timezone.
//  Also verify on BE that timeZone is always populated.
export const getHourFromUnix = (
  unixTimestamp: number,
  timeZone?: string,
): string => {
  if (!unixTimestamp) {
    return "";
  }

  const date = new Date(unixTimestamp * 1000);
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: timeZone || undefined,
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod");

  return `${hour}:${minute} ${dayPeriod}`;
};

/**
 * @param {Number} unix
 * @return {String} string Thursday, Apr 24 at 11:00 am
 */

export const fromUnixToFulldateStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  // const weekday = date.toLocaleString("en-us", { weekday: "long" });

  const options = {
    weekday: "long" as "long",
    month: "long" as "long",
    day: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);


  return formatter.format(date);

  // return `${weekday}, ${MONTHS_NAME[date.getMonth()]} ${date.getDate()}`;
};
export const fromUnixToFullVisitdateStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  // const weekday = date.toLocaleString("en-us", { weekday: "long" });

  const options = {
    weekday: "long" as "long",
    month: "long" as "long",
    day: "numeric" as "numeric",
    year: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);


  return formatter.format(date);

  // return `${weekday}, ${MONTHS_NAME[date.getMonth()]} ${date.getDate()}`;
};

export const fromUnixToFormattedVisitdateStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  // const weekday = date.toLocaleString("en-us", { weekday: "long" });

  const options = {
    month: "long" as "long",
    day: "numeric" as "numeric",
    year: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);


  return formatter.format(date);

  // return `${weekday}, ${MONTHS_NAME[date.getMonth()]} ${date.getDate()}`;
};

/**
 * @param {Number} unix
 * @return {String} string Thu, Apr 24 at 11:00 am
 */

export const fromUnixToAbbdateStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  // const weekday = date.toLocaleString("en-us", { weekday: "short" });
  // const month= date.toLocaleString("en-us", { month: "short" });

  const options = {
    weekday: "short" as "short",
    month: "short" as "short",
    day: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);

  return formatter.format(date);

  // return `${weekday}, ${month} ${date.getDate()}`;
};
export const fromUnixToMonthDayStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  // const weekday = date.toLocaleString("en-us", { weekday: "short" });
  // const month= date.toLocaleString("en-us", { month: "short" });

  const options = {
    month: "short" as "short",
    day: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);

  return formatter.format(date);

  // return `${weekday}, ${month} ${date.getDate()}`;
};

export const fromUnixToVisitdateStr = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);

  const options = {
    weekday: "short" as "short",
    month: "short" as "short",
    day: "numeric" as "numeric",
    year: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);

  return formatter.format(date);
};

export const fromUnixToDateOnly = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);

  const options = {
    day: "numeric" as "numeric",
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);

  return formatter.format(date);
};

// example return an object with keys and value
// {weekday: "Wednesday", month: "Sept", day: "15", year: "1999"}
export const fromUnixToFullDateNum = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);

  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod");
  const time = `${hour}:${minute} ${dayPeriod}`;

  return {
    weekday: getPart("weekday"),
    month: getPart("month"),
    day: getPart("day"),
    year: getPart("year"),
    time,
  };
};
export const fromUnixToShortWeekFormat = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);

  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod");
  const time = `${hour}:${minute} ${dayPeriod}`;

  return {
    weekday: getPart("weekday"),
    month: getPart("month"),
    day: getPart("day"),
    year: getPart("year"),
    time,
  };
};


/**
 * @param {Number} unix
 * @return {Number[]} [2022, 2, 30, 6, 20]
 */
export const fromUnixToArrayDates = (unix?: number): DateArray => {
  const date = unix ? new Date(unix * 1000) : new Date();

  return [date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes()];
};

/**
* @param {Number} unixTimestamp
* @return {Number} Year extracted from the Unix timestamp
*/
export const getYearFromUnixTimestamp = (unixTimestamp: number): number => {
  const date = new Date(unixTimestamp * 1000);

  return date.getFullYear();
};

export const changeTimeZone = (date: any, timeZone: string)=> {
  if (typeof date === "string") {
    return new Date(
      new Date(date).toLocaleString("en-US", {
        timeZone,
      }),
    );
  }

  return new Date(
    date.toLocaleString("en-US", {
      timeZone,
    }),
  );
};

export const fromISOToDesiredFormat = (isoTimestamp: string) => {
  try {
    const dateComponents = isoTimestamp.split("T")[0].split("-");
    const year = parseInt(dateComponents[0]);
    const month = parseInt(dateComponents[1]) - 1;
    const day = parseInt(dateComponents[2]);

    const dateObj = new Date(year, month, day);

    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const dayOfWeek = daysOfWeek[dateObj.getDay()];

    const monthName = months[month];

    return `${dayOfWeek}, ${monthName} ${day}`;
  } catch {
    return ("");
  }
};

// for example 09/15/1999
export function formatDateFromUnixTimestamp(unixTimestamp: number): string {
  const date = new Date(unixTimestamp * 1000);
  const formattedDate = (date.getUTCMonth() + 1).toString().padStart(2, "0") + "/" +
                        date.getUTCDate().toString().padStart(2, "0") + "/" +
                        date.getUTCFullYear();

  return formattedDate;
}


export function currentBookingDate(date: Date) {
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

// returned value September 15, 1999
export function dateToString(date: Date) {
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// returned value September 15, 1999
export function epochToDateString(epoch: number, timezone?: string): string {
  const date = new Date(epoch * 1000);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: timezone || "UTC",
  };

  return date.toLocaleDateString("en-US", options);
}

export const fromUnixToFullDateNumNew = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  const options = {
    day: "2-digit" as const, // day option
    month: "2-digit" as const, // month option
    year: "2-digit" as const, // year option
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  // TODO refactor this at some point
  // (Fixing it requires also updating 11 consumer call sites in WellnessMember containers.)
  const [day, month, year] = formatter.format(date).split("/");


  const dateObject = {
    month,
    day,
    year,
  };

  return dateObject;
};


export const fromUnixToNormalDate = (unix: number, timeZone?: string) => {
  const date = new Date(unix * 1000);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const parts = formatter.formatToParts(date);

  const getPart = (type: string) =>
    parts.find((p) => p.type === type)?.value || "";

  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod");
  const time = `${hour}:${minute} ${dayPeriod}`;

  return {
    weekday: getPart("weekday"),
    month: getPart("month"),
    day: getPart("day"),
    year: getPart("year"),
    time,
  };
};

export const fromISOToOverdueDateFormat = (isoDate: string): string => {
  const [year, month, day] = isoDate.split("-");
  const monthName = MONTHS_NAME[parseInt(month, 10) - 1];

  return `${monthName} ${parseInt(day, 10)}, ${year}`;
};

export const getEpochForTimeZone=()=> {
  return Math.floor(new Date().getTime() / 1000); // seconds
};

export const isWithin30DaysFromNow = (targetEpoch: string) => {
  const nowEpoch = getEpochForTimeZone();
  const secondsIn30Days = 30 * 24 * 60 * 60;

  return (parseInt(targetEpoch, 10) - nowEpoch) <= secondsIn30Days;
};

export const isWithinPast30Days = (targetEpoch: string) => {
  const nowEpoch = getEpochForTimeZone();
  const secondsIn30Days = 30 * 24 * 60 * 60;

  const target = parseInt(targetEpoch, 10);

  return nowEpoch >= target && (nowEpoch - target) <= secondsIn30Days;
};

export const isWithinPast3Days = (targetEpoch: string) => {
  const nowEpoch = getEpochForTimeZone();
  const secondsIn3Days = 3 * 24 * 60 * 60;

  const target = parseInt(targetEpoch, 10);

  return nowEpoch >= target && (nowEpoch - target) <= secondsIn3Days;
};

export const isDateWithinFiveDays = (date: string) => {
  const currentDate = getEpochForTimeZone();
  const secondsIn5Days = 5 * 24 * 60 * 60;

  const target = parseInt(date, 10);

  return target >= currentDate && (target - currentDate) <= secondsIn5Days;
};

export const isBetween2And30DaysBeforeNow = (targetEpoch: string): boolean => {
  const nowEpoch = getEpochForTimeZone();
  const seconds = parseInt(targetEpoch, 10);

  const diffInSeconds = nowEpoch - seconds;

  const secondsIn2Days = 2 * 24 * 60 * 60;
  const secondsIn30Days = 30 * 24 * 60 * 60;

  return diffInSeconds > secondsIn2Days && diffInSeconds <= secondsIn30Days;
};

export const isTargetTimeInPastForTimeZone = (targetEpoch: string): boolean => {
  const nowEpoch = getEpochForTimeZone();
  const targetEpochNumber = parseInt(targetEpoch, 10);

  return targetEpochNumber < nowEpoch;
};

export const hasEndDateElapsed = (endDate: string): boolean => {
  const nowEpoch = getEpochForTimeZone();

  return nowEpoch > parseInt(endDate.toString(), 10);
};


/**
 * Checks if 30 days have passed
 * @param {string} targetEpoch
 * @return {boolean}
 */
export const has30DaysPassed = (targetEpoch: string): boolean => {
  const nowEpoch = getEpochForTimeZone();
  const secondsIn30Days = 30 * 24 * 60 * 60;

  return (nowEpoch - parseInt(targetEpoch, 10)) > secondsIn30Days;
};

/**
 * Calendar days from today (local time) until the given epoch.
 * Negative when the date is in the past.
 * @param {number} epochSeconds
 * @param {Date} now
 * @return {number}
 */
export const getDaysUntil = (epochSeconds: number, now: Date = new Date()): number => {
  const target = new Date(epochSeconds * 1000);
  const targetDay = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

  return Math.round((targetDay - today) / (24 * 60 * 60 * 1000));
};

export const isTodayInTimeZone = (
  epochSeconds: number,
  timeZone: string,
): boolean => {
  const target = fromUnixToFullDateNum(epochSeconds, timeZone);
  const now = fromUnixToFullDateNum(
    Math.floor(Date.now() / 1000), timeZone,
  );

  return target.day === now.day &&
    target.month === now.month &&
    target.year === now.year;
};
